'use client';

import { Suspense, useCallback, useEffect, useRef, useState } from 'react';

import cls from 'classnames';

import { ACTIVITY_DEFAULTS, FALLBACK_ACTIVITY_TYPE } from '@/activity-orchestration/defaults';
import { resolveWidgetShowTrigger } from '@/activity-orchestration/track/resolve-widget-show-trigger';
import {
    trackWidgetRetentionClick,
    trackWidgetRetentionShow,
} from '@/activity-orchestration/track/track-widget-retention-show';
import { hasWidgetShowTracked, pruneWidgetShowTracked } from '@/activity-orchestration/track/widget-show-track-cache';
import { isActivityShowable, useActivity, useActivityStore } from '@/store/activity';
import { useActivityQueueStore } from '@/store/activity-queue';
import { useActivitySessionStore, useVisibleWidgets } from '@/store/activity-session';
import { generateUUID } from '@/utils/device/generate-uuid';

import { getWidgetRenderer } from './registry';

import type { IQueueTask, TKnownActivityPopupType, TWidgetShowTrigger } from '@/activity-orchestration/types';

/** 滚动停止后延迟展开（ms），与动效时长解耦 */
const SCROLL_IDLE_MS = 150;

/** 渲染可见挂件，并统一处理曝光、复弹、前台校时和滚动收起。 */

/**
 * 从 ActivityStore 组装复弹 task。
 * @param activityId 活动 id
 * @returns 可入队 task；不可展示时返回 null
 */
function buildReopenTask(activityId: string): IQueueTask | null {
    const activity = useActivityStore.getState().getActivity(activityId);
    if (!activity || !isActivityShowable(activity)) {
        return null;
    }

    const known = activity.type as TKnownActivityPopupType;
    const defaults = known in ACTIVITY_DEFAULTS ? ACTIVITY_DEFAULTS[known] : ACTIVITY_DEFAULTS[FALLBACK_ACTIVITY_TYPE];

    return {
        id: generateUUID(),
        activityId,
        type: activity.type,
        priority: activity.priority,
        tabs: [...defaults.tabs],
        source: 'offer_resolve',
        mutexGroup: defaults.mutexGroup,
        blocksOthers: defaults.blocksOthers,
        openWidgetOnDismiss: defaults.openWidgetOnDismiss,
        createdAt: Date.now(),
    };
}

interface IWidgetItemProps {
    activityId: string;
    onClick: (activityId: string) => void;
}

/** 按 activity.type 选 renderer；无匹配跳过 */
function ActivityWidgetItem(props: IWidgetItemProps) {
    const { activityId, onClick } = props;
    const activity = useActivity(activityId);
    if (!activity) {
        return null;
    }

    const Renderer = getWidgetRenderer(activity.type);
    if (!Renderer) {
        return null;
    }

    return (
        <div className='pointer-events-auto'>
            <Suspense fallback={null}>
                <Renderer activityId={activityId} onClick={() => onClick(activityId)} />
            </Suspense>
        </div>
    );
}

export default function ActivityWidgetHost() {
    const visibleWidgets = useVisibleWidgets();
    const sessions = useActivitySessionStore(state => state.sessions);
    // 滚动中半收起：true → right -35px；停止后展开：false → right 0
    const [collapsed, setCollapsed] = useState(false);
    // 上一帧可见的 activityId，用于识别「新进入可见」vs 持续可见（仅防同挂载期内重复）
    const prevVisibleIdsRef = useRef<Set<string>>(new Set());
    // 当次曝光的 trigger_scene，点击上报时复用（与 show 口径一致）
    const lastTriggerSceneRef = useRef<Map<string, TWidgetShowTrigger>>(new Map());

    // 可见列表进出 → 推导 trigger_scene 并上报 widget_retention_show
    useEffect(() => {
        const prevVisibleIds = prevVisibleIdsRef.current;
        const nextVisibleIds = new Set(visibleWidgets.map(session => session.activityId));

        visibleWidgets.forEach(session => {
            const { activityId, widgetSource } = session;
            // 持续可见：不重复曝光
            if (prevVisibleIds.has(activityId)) {
                return;
            }

            // 模块级缓存：Host 进 reader/pay 卸载后再挂载仍保留，避免误报 cold_start
            const hasTrackedShow = hasWidgetShowTracked(activityId);
            // 新进入可见：首次按写入路径映射；再次进入一律 tab_switch
            const triggerScene = resolveWidgetShowTrigger(widgetSource, hasTrackedShow);
            trackWidgetRetentionShow({ activityId, triggerScene });
            lastTriggerSceneRef.current.set(activityId, triggerScene);
        });

        prevVisibleIdsRef.current = nextVisibleIds;
    }, [visibleWidgets]);

    // session 已不在 SessionMap → 清曝光记录，避免 activityId 复用误判
    useEffect(() => {
        const aliveIds = new Set(Object.keys(sessions));
        pruneWidgetShowTracked(aliveIds);
        lastTriggerSceneRef.current.forEach((_, activityId) => {
            if (!aliveIds.has(activityId)) {
                lastTriggerSceneRef.current.delete(activityId);
            }
        });
    }, [sessions]);

    // 回前台：全量 re-check 过期（§6.8）
    useEffect(() => {
        const onVisibility = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            const { activities, expireActivity } = useActivityStore.getState();
            const now = Date.now();
            Object.values(activities).forEach(activity => {
                if (activity.status === 'active' && activity.expireAt != null && activity.expireAt <= now) {
                    expireActivity(activity.activityId);
                }
            });

            const { current, dismiss } = useActivityQueueStore.getState();
            if (current) {
                const curActivity = useActivityStore.getState().getActivity(current.activityId);
                if (!isActivityShowable(curActivity, now)) {
                    dismiss('expire', current.id);
                }
            }
        };

        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, []);

    // 页面/Tab 内滚动容器滚动时半收起，停止 SCROLL_IDLE_MS 后展开
    useEffect(() => {
        // 滚动停止计时器
        let idleTimer: ReturnType<typeof setTimeout> | null = null;

        const onScroll = () => {
            setCollapsed(true);
            if (idleTimer != null) {
                clearTimeout(idleTimer);
            }
            idleTimer = setTimeout(() => {
                setCollapsed(false);
                idleTimer = null;
            }, SCROLL_IDLE_MS);
        };

        // capture：Tab 页多为内部 overflow 容器滚动，需捕获冒泡不到 window 的 scroll
        document.addEventListener('scroll', onScroll, { passive: true, capture: true });
        return () => {
            document.removeEventListener('scroll', onScroll, { capture: true });
            if (idleTimer != null) {
                clearTimeout(idleTimer);
            }
        };
    }, []);

    const handleWidgetClick = useCallback((activityId: string) => {
        // 复用当次曝光 trigger_scene；极端未记到时兜底 tab_switch
        const triggerScene = lastTriggerSceneRef.current.get(activityId) ?? 'tab_switch';
        trackWidgetRetentionClick({ activityId, triggerScene });

        const task = buildReopenTask(activityId);
        if (!task) {
            return;
        }

        const { enqueue, tryShow, activeScene } = useActivityQueueStore.getState();
        enqueue(task);
        tryShow(activeScene ?? undefined, { force: true });
    }, []);

    if (visibleWidgets.length === 0) {
        return null;
    }

    return (
        <div
            className={cls(
                'pointer-events-none fixed bottom-[calc(52px_+_env(safe-area-inset-bottom,0px)_+_136px)] z-[40] flex flex-col items-end transition-[right] duration-500 ease-in-out',
                collapsed ? 'right-[-35px]' : 'right-0'
            )}
        >
            {visibleWidgets.map(session => (
                <ActivityWidgetItem
                    key={session.activityId}
                    activityId={session.activityId}
                    onClick={handleWidgetClick}
                />
            ))}
        </div>
    );
}
